'use client';

import {NAVIGATE_HISTORY} from '@shared/HistoryLayout/HistoryLayout';
import {COOKIE_YM_UID, getCookie} from '@utils/cookies';
import {safeSessionStorage} from '@utils/safeStorages';
import {nanoid} from 'nanoid';

import {PREV_SCREEN_INDEX} from '@web-landing/constants';

import {getOid, getOidSegments} from '@utils/omicron/getOidSegments';

import {formatDate, formatTime, MY_TRACKER_ID} from '@utils/analytics';

import {EventName, EVENTS} from '../events';
import type {AnalyticsEvent, AnalyticsEventParams} from '../types/analytics';

const previousEvent: Partial<
  Pick<AnalyticsEventParams, 'event_name' | 'screen_name'>
> = {
  event_name: undefined,
  screen_name: undefined,
};

let tmrDebug = false;

export const enableTmrDebugging = () => {
  tmrDebug = true;
};

export const logTmrEventAdapter = ({
  logEvent,
  params,
}: {
  logEvent: EventName | string;
  params?: Record<string, unknown>;
}) => logTmrEvent(logEvent, params);

export const logTmrEvent = (
  event: EventName | string,
  params: Partial<AnalyticsEventParams> = {},
) => {
  if (!window._tmr) return;

  let prevScreen;

  try {
    const arr = JSON.parse(
      safeSessionStorage.getItem(NAVIGATE_HISTORY) as string,
    );
    prevScreen = arr[arr.length - PREV_SCREEN_INDEX];
  } catch (e) {
    prevScreen = undefined;
  }

  const segments = getOidSegments();
  const userOid = getOid();

  const currentDate = new Date();

  const baseParams: AnalyticsEventParams = {
    date: formatDate(currentDate),
    event_name: event,
    fingerprint: getCookie(COOKIE_YM_UID),
    id: nanoid(),
    kid_mode: null,
    previous_event: previousEvent.event_name ?? null,
    previous_screen: `${document.location.origin}${prevScreen}` ?? null,
    product: null,
    screen_name: document.location.href.split('?')[1] ?? null,
    session_id: null,
    tab_name: null,
    time: formatTime(currentDate),
    type: null,
    user_id: userOid,
    vk_id: '-1',
    pageURL: document.location.href.split('?')[0],
    ...segments,
  };

  const analyticsEvent: AnalyticsEvent = {
    id: MY_TRACKER_ID,
    type: 'reachGoal',
    goal: event,
    params: {
      ...baseParams,
      ...params,
    },
  };

  if (tmrDebug) {
    // eslint-disable-next-line no-console
    console.log(
      `TMR: log event = ${event}, params = ${JSON.stringify(
        analyticsEvent.params,
      )}`,
    );
  }

  window._tmr.push(analyticsEvent);

  previousEvent.event_name = baseParams.event_name;
  previousEvent.screen_name = baseParams.screen_name;
};

export const emitTmrEvent = (params: Partial<AnalyticsEventParams> = {}) => {
  if (!window._tmr) {
    return;
  }

  const currentDate = new Date();

  const baseParams = {
    event_timestamp: `${formatDate(currentDate)} ${formatTime(currentDate)}`,
  };

  logTmrEvent(EVENTS.mt_pixel, {
    ...baseParams,
    ...params,
  });
};
